import {
  Grid,
  Column,
  Heading,
  Button,
  TextInput,
  FormGroup,
  InlineNotification,
  Tile,
  Content,
} from '@carbon/react';
import { getFormProps, getInputProps, useForm } from '@conform-to/react';
import { getZodConstraint, parseWithZod } from '@conform-to/zod';
import { type SEOHandle } from '@nasa-gcn/remix-seo';
import * as E from '@react-email/components';
import { data, redirect, Form, useSearchParams } from 'react-router';
import { HoneypotInputs } from 'remix-utils/honeypot/react';
import { z } from 'zod';
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx';
import { requireAnonymous } from '#app/utils/auth.server.ts';
// import {
//   ProviderConnectionForm,
//   providerNames,
// } from '#app/utils/connections.tsx';
import { prisma } from '#app/utils/db.server.ts';
import { sendEmail } from '#app/utils/email.server.ts';
import { checkHoneypot } from '#app/utils/honeypot.server.ts';
import { useIsPending } from '#app/utils/misc.tsx';
import { getSystemName } from '#app/utils/system-name.server.ts';
import { EmailSchema } from '#app/utils/user-validation.ts';
import { type Route } from './+types/nexus.ts';
import { prepareVerification } from './verify.server.ts';

export const handle: SEOHandle = {
  getSitemapEntries: () => null,
};

const SignupSchema = z.object({
  email: EmailSchema,
});

export async function loader({ request }: Route.LoaderArgs) {
  await requireAnonymous(request);
  const systemName = await getSystemName();
  return { systemName };
}

export async function action({ request }: Route.ActionArgs) {
  const formData = await request.formData();

  await checkHoneypot(formData);
  const systemName = await getSystemName();

  const submission = await parseWithZod(formData, {
    schema: SignupSchema.superRefine(async (data, ctx) => {
      const existingUser = await prisma.user.findUnique({
        where: { email: data.email },
        select: { id: true },
      });
      if (existingUser) {
        ctx.addIssue({
          path: ['email'],
          code: z.ZodIssueCode.custom,
          message: 'A user already exists with this email',
        });
        return;
      }
    }),
    async: true,
  });
  if (submission.status !== 'success') {
    return data(
      { result: submission.reply() },
      { status: submission.status === 'error' ? 400 : 200 },
    );
  }
  const { email } = submission.value;
  const { verifyUrl, redirectTo, otp } = await prepareVerification({
    period: 10 * 60,
    request,
    type: 'onboarding',
    target: email,
  });

  const response = await sendEmail({
    to: email,
    subject: `Welcome to ${systemName}!`,
    react: (
      <SignupEmail
        onboardingUrl={verifyUrl.toString()}
        otp={otp}
        systemName={systemName}
      />
    ),
  });

  if (response.status === 'success') {
    return redirect(redirectTo.toString());
  } else {
    return data(
      {
        result: submission.reply({ formErrors: [response.error.message] }),
      },
      {
        status: 500,
      },
    );
  }
}

export function SignupEmail({
  onboardingUrl,
  otp,
  systemName,
}: {
  onboardingUrl: string;
  otp: string;
  systemName: string;
}) {
  return (
    <E.Html lang="en" dir="ltr">
      <E.Container>
        <h1>
          <E.Text>Welcome to {systemName}!</E.Text>
        </h1>
        <p>
          <E.Text>
            Here's your verification code: <strong>{otp}</strong>
          </E.Text>
        </p>
        <p>
          <E.Text>Or click the link to get started:</E.Text>
        </p>
        <E.Link href={onboardingUrl}>{onboardingUrl}</E.Link>
      </E.Container>
    </E.Html>
  );
}

export default function SignupRoute({
  actionData,
  loaderData,
}: Route.ComponentProps) {
  const isPending = useIsPending();
  const [searchParams] = useSearchParams();
  // const redirectTo = searchParams.get('redirectTo');
  const { systemName } = loaderData;

  const [form, fields] = useForm({
    id: 'signup-form',
    constraint: getZodConstraint(SignupSchema),
    lastResult: actionData?.result,
    onValidate({ formData }) {
      const result = parseWithZod(formData, { schema: SignupSchema });
      return result;
    },
    shouldRevalidate: 'onBlur',
  });

  return (
    <>
      <title>{`Sign Up | ${systemName}`}</title>
      <Content className="min-h-screen bg-gray-900 !mt-0">
        <Grid className="">
          <Column sm={4} md={8} lg={16} className="!mx-0">
            <div className="flex min-h-screenx justify-center items-centerx mt-52">
              <Tile className="p-8 max-w-md w-full">
                <div className="flex items-center justify-center gap-2 mb-6">
                  <img
                    src="/img/cb-logo-black.png"
                    alt="CircuitBread Logo"
                    className="h-8 w-auto"
                  />
                  <span className="text-2xl font-bold text-white">
                    {systemName}
                  </span>
                </div>

                <div className="text-center mb-6">
                  <Heading className="mb-4">
                    Welcome! <br />
                    Let's get started!
                  </Heading>
                  <p className="text-gray-400">Please enter your email.</p>
                </div>

                <Form method="POST" {...getFormProps(form)}>
                  <HoneypotInputs />

                  {form.errors && form.errors.length > 0 && (
                    <div className="mb-4">
                      <InlineNotification
                        kind="error"
                        title="Error"
                        subtitle={form.errors.join(', ')}
                        hideCloseButton
                      />
                    </div>
                  )}

                  <FormGroup legendText="">
                    <TextInput
                      labelText="Email"
                      placeholder="Enter your email address"
                      autoComplete="email"
                      autoFocus
                      invalid={!!fields.email.errors?.length}
                      invalidText={fields.email.errors?.[0]}
                      {...getInputProps(fields.email, { type: 'email' })}
                    />
                  </FormGroup>

                  <Button
                    type="submit"
                    kind="primary"
                    size="lg"
                    disabled={isPending}
                    className="disabled:text-[#4E5A66] hover:disabled:text-[#4E5A66] mt-6"
                  >
                    {isPending ? 'Creating account...' : 'Submit'}
                  </Button>
                </Form>

                {/* {providerNames.length > 0 && (
                <>
                  <div className="my-6 relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-600"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="bg-gray-900 px-4 text-gray-400">
                        Or continue with
                      </span>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {providerNames.map((providerName) => (
                      <div key={providerName}>
                        <ProviderConnectionForm
                          type="Signup"
                          providerName={providerName}
                          redirectTo={redirectTo}
                        />
                      </div>
                    ))}
                  </div>
                </>
              )} */}
              </Tile>
            </div>
          </Column>
        </Grid>
      </Content>
    </>
  );
}

export function ErrorBoundary() {
  return <GeneralErrorBoundary />;
}
